package top.wcpe.allincore.ui.runtime.ir

import kotlin.jvm.JvmStatic
import kotlin.math.floor

/**
 * DSL range 求值结果：表示 `a..b`（闭区间）或 `a until b`（半开）。
 *
 * 步进固定为 1，要求 start/end 均为整数值（`v == floor(v)`），
 * 否则在构造时抛 [DslRuntimeError]。生成的元素以 [Double] 返回，
 * 与 DSL 数值统一为 Double 的约定一致。
 */
class IrRange private constructor(
    private val startInclusive: Long,
    // 半开转闭后的 end（最后一个元素），空 range 时 endInclusive < startInclusive。
    private val endInclusive: Long,
    private val empty: Boolean,
) : Iterable<Any> {

    fun isEmpty(): Boolean = empty

    override fun iterator(): Iterator<Any> {
        if (empty) {
            return object : Iterator<Any> {
                override fun hasNext(): Boolean = false
                override fun next(): Any = throw NoSuchElementException()
            }
        }
        return object : Iterator<Any> {
            private var cursor = startInclusive
            override fun hasNext(): Boolean = cursor <= endInclusive
            override fun next(): Any {
                if (!hasNext()) {
                    throw NoSuchElementException()
                }
                return (cursor++).toDouble()
            }
        }
    }

    companion object {
        /** 构建闭区间 [start, end]。 */
        @JvmStatic
        fun closed(start: Double, end: Double): IrRange {
            val s = toLongOrFail(start, "range start")
            val e = toLongOrFail(end, "range end")
            return IrRange(s, e, e < s)
        }

        /** 构建半开区间 [start, end)。 */
        @JvmStatic
        fun halfOpen(start: Double, end: Double): IrRange {
            val s = toLongOrFail(start, "range start")
            val e = toLongOrFail(end, "range end")
            // 半开：endInclusive = e - 1；当 s >= e 时为空
            return IrRange(s, e - 1, s >= e)
        }

        private fun toLongOrFail(v: Double, label: String): Long {
            if (v.isNaN() || v.isInfinite() || v != floor(v)) {
                throw DslRuntimeError("$label 必须为整数值，实际: $v")
            }
            if (v < Long.MIN_VALUE.toDouble() || v > Long.MAX_VALUE.toDouble()) {
                throw DslRuntimeError("$label 超出 long 范围: $v")
            }
            return v.toLong()
        }
    }
}
